/**
 * @creditnirvana/cobrowse
 *
 * Guided assistance for web journeys. The assistant sees the STRUCTURE of the page, never its
 * contents, and highlights the next step for the customer.
 *
 * Design rule that outranks every other consideration in this file: the SDK must never break
 * the host application. Every public entry point is wrapped so that an internal failure
 * disables assistance and leaves the page untouched.
 */
import type {
  CoBrowseConfig, CoBrowseError, CoBrowseHandle, CoBrowseStatus,
  PrivacyRules, SessionEndReason, SessionInfo, UiOptions,
} from "./types.js";
import { PageScanner } from "./page-model.js";
import { Overlay } from "./overlay.js";
import { Transport, type Inbound } from "./transport.js";

export * from "./types.js";

import { VERSION } from "./version.js";
export { VERSION };

const DEFAULT_ENDPOINT = "https://cobrowse.unikernel.ai";
const DEFAULT_LINK_PARAM = "cb";
const IDLE_MS = 5 * 60_000;         // matches the service-side reap
const RESCAN_DEBOUNCE_MS = 400;
/**
 * Per-tab memory of the active session. A journey that spans pages arrives with the reference
 * only on the FIRST url; without this, page two would have nothing to bind to and assistance
 * would silently stop mid-payment. sessionStorage (not a cookie, not localStorage) is scoped
 * to the tab and cleared when it closes, and holds only the opaque session reference plus
 * whether consent was already given, so the customer is not asked again on every page.
 */
const STORE_KEY = "cn_cobrowse_session";
function remember(id: string, consented: boolean): void {
  try { sessionStorage.setItem(STORE_KEY, JSON.stringify({ id, consented })); } catch { /* storage blocked */ }
}
function recall(): { id: string; consented: boolean } | null {
  try {
    const raw = sessionStorage.getItem(STORE_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw);
    return typeof v?.id === "string" ? { id: v.id, consented: !!v.consented } : null;
  } catch { return null; }
}
function forget(): void { try { sessionStorage.removeItem(STORE_KEY); } catch { /* storage blocked */ } }

function supported(): boolean {
  return typeof window !== "undefined" && typeof document !== "undefined" &&
    typeof WebSocket !== "undefined" && typeof Element.prototype.attachShadow === "function";
}

/** Read a session reference from the URL: the link the assistant sent by SMS or email. */
export function sessionRefFromUrl(param: string = DEFAULT_LINK_PARAM, href?: string): string | null {
  try {
    const u = new URL(href ?? window.location.href);
    const v = u.searchParams.get(param);
    return v && /^[A-Za-z0-9_-]{4,64}$/.test(v) ? v : null;
  } catch { return null; }
}

class Session implements CoBrowseHandle {
  readonly version = VERSION;
  private state: CoBrowseStatus = "idle";
  private info: SessionInfo | null = null;
  private scanner: PageScanner;
  private overlay: Overlay;
  private transport?: Transport;
  private observer?: MutationObserver;
  private rescanTimer = 0;
  private idleTimer = 0;
  private lastSignature = "";
  private consented = false;
  /** Page listeners are registered once per session, not once per connection. */
  private watching = false;

  constructor(private cfg: CoBrowseConfig) {
    this.scanner = new PageScanner(cfg.privacy);
    this.overlay = new Overlay({
      highlightColor: cfg.ui?.highlightColor,
      zIndex: cfg.ui?.zIndex,
      audioCue: cfg.ui?.audioCue,
      announce: cfg.ui?.announce,
    });
  }

  // ── public API ────────────────────────────────────────────────────────────
  status(): CoBrowseStatus { return this.state; }
  session(): SessionInfo | null { return this.info; }

  updatePrivacy(rules: PrivacyRules): void {
    this.guard(() => {
      this.cfg.privacy = rules;
      this.scanner.update(rules);
      if (this.state === "active") this.publish(true);
    });
  }

  async start(): Promise<void> {
    if (this.state === "active" || this.state === "connecting") return;
    try {
      const id = await this.resolveSessionId();
      if (!id) return;                                  // resolveSessionId already reported why
      if (!(await this.requestConsent(id))) {
        this.report({ code: "consent_declined", message: "The customer declined assistance.", fatal: false });
        return;
      }
      this.state = "connecting";
      this.info = { id, startedAt: Date.now() };
      remember(id, true);
      this.openTransport(id);
    } catch (e) {
      this.report({ code: "internal", message: String((e as Error)?.message ?? e), fatal: true });
      this.teardown("error");
    }
  }

  end(reason: SessionEndReason = "ended_by_user"): void {
    this.guard(() => this.teardown(reason));
  }

  // ── internals ─────────────────────────────────────────────────────────────
  /** Never let an internal fault escape into the host application. */
  private guard(fn: () => void): void {
    try { fn(); } catch (e) {
      this.report({ code: "internal", message: String((e as Error)?.message ?? e), fatal: false });
    }
  }

  private report(err: CoBrowseError): void {
    if (this.cfg.debug) console.warn(`[cobrowse] ${err.code}: ${err.message}`);
    try { this.cfg.onError?.(err); } catch { /* host callback must not break us either */ }
  }

  /** A session id comes from the link the customer opened, or from your backend's token. */
  private async resolveSessionId(): Promise<string | null> {
    const param = this.cfg.linkParam === null ? null : (this.cfg.linkParam || DEFAULT_LINK_PARAM);
    if (param) {
      const fromLink = sessionRefFromUrl(param);
      if (fromLink) return fromLink;
      // Same journey, later page, no token in the url: resume the tab's session.
      const prior = recall();
      if (prior) { this.consented = prior.consented; return prior.id; }
    }
    const t = this.cfg.sessionToken;
    try {
      const token = typeof t === "function" ? await t() : t;
      if (token) return String(token);
    } catch (e) {
      this.report({ code: "token_invalid", message: `Token could not be obtained: ${String((e as Error)?.message ?? e)}`, fatal: false });
      return null;
    }
    this.report({ code: "token_invalid", message: "No session token and no session reference in the URL.", fatal: false });
    return null;
  }

  private async requestConsent(id: string): Promise<boolean> {
    if (this.consented) return true;
    let ok = false;
    if (this.cfg.onConsentRequired) {
      try { ok = !!(await this.cfg.onConsentRequired({ tenant: this.cfg.tenant, sessionId: id })); }
      catch { ok = false; }
    } else if ((this.cfg.ui?.consent ?? "builtin") === "builtin") {
      ok = await this.overlay.consent();
    } else {
      ok = false;   // ui.consent="custom" with no handler: refuse rather than assume consent
      this.report({ code: "internal", message: 'ui.consent is "custom" but onConsentRequired was not provided.', fatal: false });
    }
    this.consented = ok;
    return ok;
  }

  private openTransport(id: string): void {
    const endpoint = this.cfg.endpoint || DEFAULT_ENDPOINT;
    this.transport = new Transport(endpoint, id, {
      onOpen: () => {
        const recovered = this.state === "degraded";
        this.state = "active";
        if ((this.cfg.ui?.indicator ?? "builtin") === "builtin") {
          this.overlay.showIndicator(() => this.end("ended_by_user"));
        }
        this.watchPage();
        // Force the publish: after a reconnect the page may be identical to the one we
        // last sent, and the unchanged-signature short circuit would leave the service
        // with nothing while the assistant waits to be told where the customer is.
        this.publish(true);
        this.bumpIdle();
        if (recovered) {
          try { this.cfg.onSessionRecovered?.(this.info!); } catch { /* host callback */ }
        } else {
          try { this.cfg.onSessionStart?.(this.info!); } catch { /* host callback */ }
        }
      },
      onMessage: (m) => this.guard(() => this.handle(m)),
      onDrop: () => this.guard(() => {
        if (this.state !== "active") return;
        this.state = "degraded";
        // The rings are following elements whose page we can no longer hear about. Leave
        // them: a stale highlight the customer is already looking at is less confusing
        // than the page silently going blank while the assistant is mid-sentence.
        try { this.cfg.onSessionDegraded?.(this.info!); } catch { /* host callback */ }
      }),
      onFail: (why) => {
        this.report(why === "rejected"
          ? { code: "token_invalid", message: "The service rejected this session.", fatal: true }
          : { code: "network_unavailable", message: "The assistance service is unreachable.", fatal: true });
        this.teardown("disconnected");
      },
    }, !!this.cfg.debug);
    this.transport.connect();
  }

  private handle(m: Inbound): void {
    this.bumpIdle();
    if (m.t === "done") { this.teardown("completed"); return; }
    if (m.t !== "annotate") return;
    const a = m.annotation;
    if (!a || a.kind === "clear") { this.overlay.clearHighlight(); return; }

    // A CHOICE step points at several controls at once. Resolve each; ring the ones that
    // exist. Partial success is still useful — five of six banks on screen beats none —
    // so the ack reports how many landed rather than a bare boolean.
    if (a.kind === "highlightMany") {
      const els: HTMLElement[] = [];
      for (const label of a.labels || []) {
        const found = label ? this.scanner.resolve(label) : null;
        if (found && !els.includes(found)) els.push(found);
      }
      this.overlay.highlightAll(els);
      this.transport?.send({ t: "highlight_ack", ack: {
        highlightId: a.highlightId,
        rendered: els.length > 0,
        count: els.length,
        requested: (a.labels || []).length,
      } });
      return;
    }

    // Resolve by LABEL first: it is the semantic contract and survives re-renders. The
    // service also sends a selector it derived from its own view of the page; fall back to
    // that only if the label cannot be matched locally.
    let el = a.label ? this.scanner.resolve(a.label) : null;
    if (!el && a.sel) { try { el = document.querySelector<HTMLElement>(a.sel); } catch { el = null; } }
    this.overlay.highlight(el);
    // Tell the service whether it actually rendered, so "shown and ignored" is
    // distinguishable from "never shown".
    this.transport?.send({ t: "highlight_ack", ack: { highlightId: a.highlightId, rendered: !!el } });
  }

  /**
   * Re-publish on navigation and on meaningful DOM change. Never on a timer.
   *
   * Registered once per session. This runs from the transport's open callback, which
   * fires again on every reconnect, so without the guard a customer who rode out three
   * drops would have four MutationObservers and four click listeners, each publishing
   * the same model and emitting the same interaction event four times over.
   */
  private watchPage(): void {
    if (this.watching) return;
    this.watching = true;
    const schedule = () => {
      window.clearTimeout(this.rescanTimer);
      this.rescanTimer = window.setTimeout(() => this.guard(() => this.publish(false)), RESCAN_DEBOUNCE_MS);
    };
    this.observer = new MutationObserver(schedule);
    try {
      this.observer.observe(document.body, { childList: true, subtree: true, attributes: true,
        attributeFilter: ["disabled", "aria-disabled", "hidden", "style", "class"] });
    } catch { /* body not ready: the navigation hooks below still cover us */ }
    window.addEventListener("popstate", schedule);
    window.addEventListener("hashchange", schedule);

    /**
     * Single-page navigation.
     *
     * `history.pushState` fires NO event, which is how every React/Vue router moves
     * between steps. So a multi-step KYC or mandate journey changed screen and the SDK
     * heard nothing at all — it only noticed later, if the DOM happened to mutate
     * enough to trip the observer. Patching the two history methods is the standard
     * (and only) way to observe it. The originals are always called, so the host's
     * router is unaffected even if our listener throws.
     */
    let lastUrl = location.pathname;
    const onRoute = () => this.guard(() => {
      const to = location.pathname;
      if (to === lastUrl) return;
      const from = lastUrl;
      lastUrl = to;
      this.transport?.send({ t: "event", event: { kind: "navigated", from, to } });
      this.publish(true);           // a new screen is never an unchanged signature
    });
    for (const name of ["pushState", "replaceState"] as const) {
      try {
        const orig = history[name];
        if (typeof orig !== "function" || (orig as { __cb?: boolean }).__cb) continue;
        const patched = function (this: History, ...args: unknown[]) {
          const r = (orig as (...a: unknown[]) => unknown).apply(this, args);
          setTimeout(onRoute, 0);   // after the router has actually swapped the view
          return r;
        };
        (patched as { __cb?: boolean }).__cb = true;
        history[name] = patched as typeof orig;
      } catch { /* a locked-down history object: popstate below still covers back/forward */ }
    }
    window.addEventListener("popstate", onRoute);
    window.addEventListener("hashchange", onRoute);
    document.addEventListener("input", schedule, true);   // a field became filled
    document.addEventListener("focusin", schedule, true);

    // Interaction events. The guidance engine advances a step on evidence that the customer
    // acted: a step whose completion condition is "clicked" cannot advance from the page
    // model alone, because clicking a button changes nothing observable about it. Labels go
    // through reportableLabel, so a masked, excluded or sensitive control emits nothing.
    const INTERACTIVE_SEL = "a[href],button,input,select,textarea,[role=button],[role=link]," +
      "[role=checkbox],[role=radio],[role=tab],[role=menuitem],[onclick]";
    document.addEventListener("click", (e) => this.guard(() => {
      const el = (e.target as Element | null)?.closest?.(INTERACTIVE_SEL) as HTMLElement | null;
      if (!el) return;
      const label = this.scanner.reportableLabel(el);
      if (label) this.transport?.send({ t: "event", event: { kind: "clicked", label } });
      schedule();
    }), true);
    document.addEventListener("change", (e) => this.guard(() => {
      const el = e.target as HTMLElement | null;
      if (!el || !/^(input|select|textarea)$/i.test(el.tagName)) return;
      const label = this.scanner.reportableLabel(el);
      if (label) this.transport?.send({ t: "event", event: { kind: "input", label } });
      schedule();
    }), true);

    /**
     * The customer pressed the button and the page did nothing.
     *
     * This is the single most valuable thing the assistant can be told, and until now it
     * was invisible: a blocked submit changes no label and no filled state, so the page
     * model looked identical and the assistant kept saying "tap Pay Now" to someone who
     * had already tapped it three times. Reporting WHICH fields blocked it turns that
     * into "the expiry date and CVV are still needed".
     */
    const reportSubmitAttempt = (form?: HTMLFormElement | null): void => {
      const blockedBy = this.scanner.blockers(form ?? undefined);
      this.transport?.send({ t: "event", event: { kind: "submit_attempt", blockedBy } });
      schedule();
    };

    // A native submit that the browser let through. `submit` does NOT fire when
    // constraint validation rejects the form, which is exactly the interesting case —
    // hence the invalid handler below, which does.
    document.addEventListener("submit", () => this.guard(() => reportSubmitAttempt(null)), true);
    // Fires once per rejected field when the browser blocks a submit. Debounced into a
    // single event so a six-field form reports one attempt, not six.
    let invalidTimer = 0;
    document.addEventListener("invalid", (e) => this.guard(() => {
      const form = (e.target as HTMLInputElement | null)?.form ?? null;
      window.clearTimeout(invalidTimer);
      invalidTimer = window.setTimeout(() => this.guard(() => reportSubmitAttempt(form)), 60);
    }), true);
    // Frameworks that validate in JavaScript never fire `invalid` at all: they intercept
    // the click, decide the form is incomplete, and re-render. So a click on a submit
    // control while anything is blocking is itself an attempt worth reporting.
    document.addEventListener("click", (e) => this.guard(() => {
      const el = (e.target as Element | null)?.closest?.(
        "button[type=submit],input[type=submit],button:not([type])") as HTMLElement | null;
      if (!el) return;
      const form = (el as HTMLButtonElement).form ?? null;
      if (this.scanner.blockers(form ?? undefined).length) reportSubmitAttempt(form);
    }), true);

    /**
     * The customer switched tabs, so the page model has stopped describing what they are
     * looking at. Without this the assistant guides a screen nobody is on: "now click Pay
     * Now" — "I don't see it" — because they are three tabs away.
     */
    document.addEventListener("visibilitychange", () => this.guard(() => {
      if (document.hidden) {
        this.transport?.send({ t: "event", event: { kind: "tab_hidden" } });
      } else {
        this.transport?.send({ t: "event", event: { kind: "tab_visible" } });
        this.publish(true);   // they may have done something elsewhere; re-sync
      }
    }));
  }

  /** Publish the page model. `force` bypasses the unchanged-signature short circuit. */
  private publish(force: boolean): void {
    if (this.state !== "active" && !force) return;
    const model = this.scanner.scan();
    if (!force && model.signature === this.lastSignature) return;
    this.lastSignature = model.signature;
    this.transport?.send({ t: "pagemodel", model });
    this.bumpIdle();
  }

  private bumpIdle(): void {
    window.clearTimeout(this.idleTimer);
    this.idleTimer = window.setTimeout(() => this.guard(() => this.teardown("idle")), IDLE_MS);
  }

  private teardown(reason: SessionEndReason): void {
    if (this.state === "ended") return;
    this.state = "ended";
    this.watching = false;
    window.clearTimeout(this.idleTimer);
    window.clearTimeout(this.rescanTimer);
    try { this.observer?.disconnect(); } catch { /* not observing */ }
    this.observer = undefined;
    this.transport?.close();
    this.transport = undefined;
    this.overlay.destroy();
    // A session we gave up reaching is not a session that ENDED. The service keeps it,
    // so if the customer's connection returns and the page reloads — which is exactly
    // what someone does when a page stops responding — the journey resumes instead of
    // making them ask the agent for a new link mid-call.
    if (reason !== "disconnected") forget();
    if (this.info) {
      this.info.endedAt = Date.now();
      this.info.reason = reason;
      try { this.cfg.onSessionEnd?.(this.info); } catch { /* host callback */ }
    }
  }
}

/** A no-op handle, returned when the SDK disables itself. Keeps host code total. */
function inert(reason: CoBrowseError, cfg?: CoBrowseConfig): CoBrowseHandle {
  try { cfg?.onError?.(reason); } catch { /* host callback */ }
  return {
    version: VERSION,
    start: async () => {}, end: () => {}, updatePrivacy: () => {},
    status: () => "idle" as CoBrowseStatus, session: () => null,
  };
}

export const CoBrowse = {
  /**
   * What the page model currently sees, for the integrator to inspect. Runs a scan with
   * default privacy rules and starts nothing — no session, no network, no consent prompt.
   * Useful for checking that your controls are actually visible to the assistant before
   * you author a journey against them.
   */
  __scanForTest(): Array<{ label: string; role: string; filled?: boolean }> {
    try {
      return new PageScanner().scan().elements.map((e) => ({ label: e.label, role: e.role, filled: e.filled }));
    } catch { return []; }
  },

  /**
   * The WHOLE model the assistant would receive, optionally under a given set of privacy
   * rules. Use this to check what your own configuration actually does — including which
   * parts of your page the SDK has to report as blind, such as a payment iframe.
   * Starts nothing: no session, no network, no consent prompt.
   */
  __scanModelForTest(rules?: PrivacyRules) {
    try { return new PageScanner(rules).scan(); }
    catch { return { url: "", title: "", elements: [], signature: "" }; }
  },

  /**
   * A standalone overlay, for checking how highlights render on YOUR page without starting a
   * session. Draws only; no network, no consent, no data leaves the browser.
   */
  __overlayForTest(opts: UiOptions = {}): Overlay {
    const o = new Overlay({
      highlightColor: opts.highlightColor, zIndex: opts.zIndex,
      audioCue: opts.audioCue, announce: opts.announce,
    });
    try { (window as unknown as { __testOverlay?: Overlay }).__testOverlay = o; } catch { /* ignore */ }
    return o;
  },

  /**
   * Initialise the SDK. Resolves to a handle; never rejects and never throws, so a failure
   * here cannot take your application down with it.
   *
   * If the customer arrived from an assistant-sent link, assistance is offered automatically:
   * the session reference is read from the URL and consent is requested straight away.
   */
  async init(config: CoBrowseConfig): Promise<CoBrowseHandle> {
    try {
      if (!supported()) {
        return inert({ code: "unsupported_browser", message: "This browser does not support the SDK.", fatal: true }, config);
      }
      if (!config?.tenant) {
        return inert({ code: "internal", message: "config.tenant is required.", fatal: true }, config);
      }
      const s = new Session(config);
      // Offer assistance immediately whenever we already know WHICH session to join.
      // Two ways to know: the customer arrived on an assistant-sent link, or the host
      // page was handed a token by its own backend. The token used to be read by
      // resolveSessionId but never triggered anything, so an integrator following the
      // documented `sessionToken` pattern got a handle that sat at "idle" forever and
      // reported no error — the SDK looked installed and did nothing.
      const param = config.linkParam === null ? null : (config.linkParam || DEFAULT_LINK_PARAM);
      const fromLink = !!(param && (sessionRefFromUrl(param) || recall()));
      const fromToken = config.sessionToken != null && config.sessionToken !== "";
      if (fromLink || fromToken) {
        const run = () => { void s.start(); };
        if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", run, { once: true });
        else run();
      }
      return s;
    } catch (e) {
      return inert({ code: "internal", message: String((e as Error)?.message ?? e), fatal: true }, config);
    }
  },
  sessionRefFromUrl,
  VERSION,
};

export default CoBrowse;
